import React, { createContext, useEffect, useState, useCallback } from "react";
import TokenService from "../services/token.service";
import axiosInstance from "../services/api.service";
import secureLocalStorage from "react-secure-storage";
import moment from "moment";
import { useWindowSize } from "react-hooks-window-size";
import { toast } from "react-toastify";

const AppContext = createContext();

const AppProvider = (props) => {
	const screenSize = useWindowSize();
	const desktopView = screenSize.width > 968;
	const mobileView = screenSize.width < 968;

	const token = secureLocalStorage.getItem("token");
	const User = useState(TokenService.getUser());
	const [today, setToday] = useState(null);
	const [loadingAllEvents, setLoadingAllEvents] = useState(false);
	const [resetStatusToggle, setResetStatusToggle] = useState(false);
	const [resetSearchBar, setResetSearchBar] = useState(false);

	const [allEventData, setAllEventData] = useState([]);
	const [filteredEventData, setFilteredEventData] = useState(allEventData);

	const customId = "toast-custom-id";

	// Default Pop Up:
	const showToast = (msg) => {
		toast(msg, {
			position: toast.POSITION.TOP_CENTER,
			toastId: customId,
			autoClose: 5000,
			hideProgressBar: true,
		});
	};

	// Success Pop Up:
	const showToastSuccess = (success) => {
		toast.success(success, {
			position: toast.POSITION.TOP_CENTER,
			toastId: customId,
			autoClose: 1500,
			hideProgressBar: true,
		});
	};

	// Warning Pop Up:
	const showToastWarning = (warning) => {
		toast.warning(warning, {
			position: toast.POSITION.TOP_CENTER,
			toastId: customId,
			autoClose: 2000,
			hideProgressBar: true,
		});
	};

	// Error Pop Up:
	const showToastError = (error) => {
		toast.error(error, {
			position: toast.POSITION.TOP_CENTER,
			toastId: customId,
			autoClose: 1500,
			hideProgressBar: true,
		});
	};

	// Info Pop Up:
	const showToastInfo = (info) => {
		toast.info(info, {
			position: toast.POSITION.TOP_CENTER,
			toastId: customId,
			autoClose: 5000,
			hideProgressBar: true,
		});
	};

	// Today's Date
	useEffect(() => {
		const time = new Date().toDateString();
		const today_date = moment(time).utc().format("yyyy-MM-DD");
		setToday(today_date);
	}, []);

	// All Events:
	const getAllEvents = useCallback(async () => {
		setLoadingAllEvents(true);

		try {
			const res = await axiosInstance.get(`/api/v1/event`);
			const resData = res?.data?.data;

			const sortedData = resData.sort((a, b) => {
				return (
					new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
				);
			});

			setAllEventData(sortedData);
			setFilteredEventData(sortedData);

			setLoadingAllEvents(false);
		} catch (error) {
			console.log(error);
			setLoadingAllEvents(false);
		}
	}, []);

	// Axios Default Headers
	useEffect(() => {
		if (token) {
			getAllEvents();
		} else {
			window.location = "/";
		}
	}, [getAllEvents, token]);

	return (
		<AppContext.Provider
			value={{
				screenSize,
				desktopView,
				mobileView,
				showToast,
				showToastSuccess,
				showToastWarning,
				showToastError,
				showToastInfo,

				resetStatusToggle,
				setResetStatusToggle,
				resetSearchBar,
				setResetSearchBar,

				allEventData,
				setAllEventData,
				filteredEventData,
				setFilteredEventData,
				getAllEvents,

				loadingAllEvents,
				setLoadingAllEvents,

				User,
				today,
			}}
		>
			{props.children}
		</AppContext.Provider>
	);
};

function useAppContext() {
	const context = React.useContext(AppContext);
	if (context === undefined) {
		throw new Error("useAppContext must be within an AppProvider");
	}
	return context;
}

export { AppProvider, useAppContext };
